import joi from "joi";
import parseTimestamp from "./parseTimestamp.js";
import { nanoid } from 'nanoid'

const unifiedSchema = joi.object({
    id: joi.string().required(),
    "@timestamp": joi.string().required(),
    message: joi.string().required(),
    origin: joi.string(),
    version: joi.string(),
    deviceType: joi.string(),
    severityLevel: joi.string().valid('INFO', 'WARN', 'ERROR', 'DEBUG').required(),
    path: joi.string(),
    sourceIPAddress: joi.string(),
    userId: joi.string(),
    appId: joi.string(),
    errorCode: joi.number().integer().min(0).strict(),
    errorMessage: joi.string(),
    jsonPayload: joi.object(),
    trace: joi.array(),
    source: joi.string(),
    apiMethod: joi.string(),
    apiHeaders: joi.object(),
    apiHost: joi.string(),
    apiResponseCode: joi.number().integer().min(0).strict(),
    apiResponseMessage: joi.string(),
    meta: joi.object(),
    environment: joi.string(),
    config: joi.object(),
})

/**
 * Verifies and enriches a log payload by adding timestamp and ID, then validates against schema
 * 
 * @param {Object} payload - The log payload to verify
 * @param {string} [payload.message] - Log message
 * @param {string} [payload.origin] - Origin of the log
 * @param {string} [payload.version] - Version information
 * @param {string} [payload.deviceType] - Type of device
 * @param {('INFO'|'WARN'|'ERROR'|'DEBUG')} payload.severityLevel - Severity level of the log
 * @param {string} [payload.path] - Path information
 * @param {string} [payload.sourceIPAddress] - Source IP address
 * @param {string} [payload.appId] - App ID
 * @param {string} [payload.userId] - User ID
 * @param {number} [payload.errorCode] - Error code if applicable
 * @param {string} [payload.errorMessage] - Error message if applicable
 * @param {string} [payload.jsonPayload] - Additional JSON payload
 * @param {Array} [payload.trace] - Trace information
 * @param {string} [payload.source] - Source of the log
 * @param {string} [payload.apiMethod] - API method if applicable
 * @param {string} [payload.apiHeader] - API header if applicable
 * @param {string} [payload.apiHost] - API host if applicable
 * @param {number} [payload.apiResponseCode] - API response code if applicable
 * @param {string} [payload.apiResponseMessage] - API response message if applicable
 * @param {Object} [payload.meta] - Additional metadata
 * @param {string} [payload.environment] - Environment information
 * @returns {Object} Object containing error message (if any) and enriched payload
 * @returns {string|undefined} return.error - Validation error message if validation fails
 * @returns {Object} return.payload - The enriched and validated payload
 */
function verifyPayload(payload) {
    payload["@timestamp"] = parseTimestamp();
    payload.id = nanoid();
    const { error } = unifiedSchema.validate(payload);
    return {error: error?.message, payload};
}

export default verifyPayload;